import React, { useState } from 'react'
import { useAtom } from 'jotai'
import Classnames from 'classnames'

// helper functions
import { returnValue } from '@/utils/op-types/helperFunctions'

// store
import {
  isBuilder,
  manifestStatic,
  pageContentStatic,
  manifestDesign,
  pageContentDesign,
  manifestBlocks,
  pageContentBlocks,
} from '@/utils/op-types/store'

// block renderer
import BlockRenderer from '@/components/optypes/frontend/BlockRenderer'

// block builder tools
import StaticContentWrapper from '@/components/optypes/frontend/StaticContentWrapper'
import AddBlockBuilder from '@/components/optypes/frontend/AddBlockBuilder'

// components
import BlocksHeader from '@/components/optypes/frontend/SimpleGridScroller/BlocksHeader'

import ReactPlayer from 'react-player'

// blocks
import Header from '@/components/optypes/frontend/SideScroller/blocks/Header'
import InputBlock from '@/components/optypes/frontend/SideScroller/blocks/InputBlock'
import ButtonBlock from '@/components/optypes/frontend/SideScroller/blocks/ButtonBlock'
import MultiChoiceBlock from '@/components/optypes/frontend/SideScroller/blocks/MultiChoiceBlock'
import LinkBlock from '@/components/optypes/frontend/SideScroller/blocks/LinkBlock'

import dynamic from 'next/dynamic'

// drag and drop context wrapper
import DragWrapper from '@/components/optypes/headlesss-components/DragWrapper'
import OnepagerFooter from '@/components/optypes/headlesss-components/OnepagerFooter'

const CardsBlock = dynamic(
  () => import('@/components/optypes/frontend/SideScroller/blocks/CardsBlock'),
  {
    ssr: false,
  }
)

const TodosBlock = dynamic(
  () => import('@/components/optypes/frontend/SideScroller/blocks/TodosBlock'),
  {
    ssr: false,
  }
)

const Scrollbars = dynamic(() => import('react-custom-scrollbars'), {
  ssr: false,
})

const Components = {
  header: Header,
  inputblock: InputBlock,
  cardblock: CardsBlock,
  todosblock: TodosBlock,
  ctablock: ButtonBlock,
  multichoiceblock: MultiChoiceBlock,
  linkblock: LinkBlock,
}

const SideScroller = () => {
  const [builder] = useAtom(isBuilder)
  // manifest settings
  const [opStatic] = useAtom(manifestStatic)
  const [opDesign] = useAtom(manifestDesign)
  const [opBlocks] = useAtom(manifestBlocks)
  // custom page settings
  const [pageStatic] = useAtom(pageContentStatic)
  const [pageDesign] = useAtom(pageContentDesign)
  const [pageBlocks] = useAtom(pageContentBlocks)

  const [fullScreen, setFullScreen] = useState(false)

  return (
    <div
      className="opr-background pb-12 min-h-screen"
      style={{
        backgroundColor: returnValue('pagebg', opDesign, pageDesign),
      }}
    >
      {/* HEADER */}
      <div
        className={Classnames(
          'opr-mainheader border-b-2 border-black border-opacity-25 relative z-10'
        )}
        style={{
          backgroundColor: returnValue('bgcolor', opDesign, pageDesign),
          color: returnValue('maintextcolor', opDesign, pageDesign),
        }}
      >
        {/* background image */}
        {returnValue('bgimgurl', opDesign, pageDesign) && (
          <div
            className="absolute top-0 left-0 z-0 h-full w-full  bg-cover bg-no-repeat"
            style={{
              backgroundImage: `url(${returnValue(
                'bgimgurl',
                opDesign,
                pageDesign
              )})`,
              opacity: returnValue('bgimgopacity', opDesign, pageDesign),
            }}
          ></div>
        )}

        <div className="container max-w-7xl mx-auto">
          <div className="flex items-center relative z-10 pt-12 pb-16">
            {/* headline / subheadline */}
            <div
              className={Classnames({
                'w-3/4 pr-12':
                  !returnValue('videourl', opStatic, pageStatic) &&
                  !returnValue('imgurl', opStatic, pageStatic),
                'w-1/2 pr-12':
                  returnValue('videourl', opStatic, pageStatic) ||
                  returnValue('imgurl', opStatic, pageStatic),
              })}
            >
              <h1
                className={Classnames('font-bold text-6xl', {
                  appTitleItem:
                    returnValue('herofont', opDesign, pageDesign) == 'sans',
                })}
                style={{
                  color: returnValue('headlinetextcolor', opDesign, pageDesign),
                }}
              >
                <StaticContentWrapper contentKey="headline">
                  {returnValue('headline', opStatic, pageStatic)}
                </StaticContentWrapper>
              </h1>
              <div className="mt-4 mb-6 text-xl font-medium">
                <StaticContentWrapper contentKey="subheadline">
                  {returnValue('subheadline', opStatic, pageStatic)}
                </StaticContentWrapper>
              </div>
            </div>

            <div
              className={Classnames({
                'w-1/2':
                  returnValue('videourl', opStatic, pageStatic) ||
                  returnValue('imgurl', opStatic, pageStatic),
              })}
            >
              {/* video */}
              {returnValue('videourl', opStatic, pageStatic) && (
                <div className="player-wrapper rounded shadow-lg overflow-hidden mb-8">
                  <ReactPlayer
                    controls={false}
                    playing={false}
                    width="100%"
                    height="100%"
                    className="react-player"
                    url={returnValue('videourl', opStatic, pageStatic)}
                  />
                </div>
              )}

              {/* img */}
              {returnValue('imgurl', opStatic, pageStatic) && (
                <img
                  src={returnValue('imgurl', opStatic, pageStatic)}
                  alt=""
                  className="rounded shadow-lg mb-8"
                />
              )}
            </div>
          </div>
        </div>
      </div>
      {/* side scroller content blocks */}
      <div
        className={Classnames(
          'opr-container container max-w-7xl mx-auto -mt-12 relative z-10',
          {}
        )}
        style={
          {
            // backgroundColor: returnValue('blockbgcolor', opDesign, pageDesign),
            // color: returnValue('blocktextcolor', opDesign, pageDesign),
          }
        }
      >
        <div className="mb-6">
          <BlocksHeader />
        </div>

        <div
          className={Classnames('grid', {
            'grid-cols-2':
              returnValue('gridamount', opDesign, pageDesign) == '2',
            'grid-cols-3':
              returnValue('gridamount', opDesign, pageDesign) == '3',
            'grid-cols-4':
              returnValue('gridamount', opDesign, pageDesign) == '4',
            'gap-3': returnValue('gridgap', opDesign, pageDesign) == 'default',
            'gap-6': returnValue('gridgap', opDesign, pageDesign) == 'lg',
            'gap-1': returnValue('gridgap', opDesign, pageDesign) == 'sm',
          })}
        >
          <DragWrapper>
            {pageBlocks.map((block, index) => (
              <BlockRenderer
                key={index}
                index={index}
                Components={Components}
                block={block}
              />
            ))}
          </DragWrapper>
        </div>

        {/* add block button builder */}
        {builder && (
          <div className="mt-6">
            <AddBlockBuilder btnText="Add Block" />{' '}
          </div>
        )}
      </div>
      <div
        className="py-12"
        style={
          {
            // color: returnValue('maintextcolor', opDesign, pageDesign),
          }
        }
      >
        <OnepagerFooter />
      </div>
    </div>
  )
}

export default SideScroller
